import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { AuthContext } from '../../context/auth/auth-context'
import axios from 'axios'
import { ExtendedPromise } from '../../utils/promises-utils'
import { formStrategyApiUrl } from '../../utils/api/servers'
import { useDebouncedCallback } from 'use-debounce'
import { useHeartbeats } from '../../hooks/data/use-heartbeats'
import { usePortfolios } from '../../hooks/data/use-portfolios'
import { DataGridPro, type GridAutosizeOptions, type GridColDef } from '@mui/x-data-grid-pro'
import { type GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro'
import { type GridRowModelUpdate } from '@mui/x-data-grid/models/gridRows'
import {
  type AlertColor, Button, Card, CardContent, Chip, Divider, Grid, IconButton, Stack, TextField, Tooltip, Typography,
  Fade,
} from '@mui/material'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import LockIcon from '@mui/icons-material/Lock'
import { formatTimeDifferenceBetweenTwoInstants, formatToLocalMilliTimestampFromUTCString } from '../../utils/time-utils'
import { formAlertErrorMessage, handleRaceSettleAllResponse, isEmptyString } from '../../utils/string-utils'
import StackColumnLayout from '../../layouts/stack-column-layout'
import TradingToggleButton from './trading-toggle-button'
import ToastWithMessage from '../../components/toast/toast-with-message'

function getHeartbeatStatusChipColour (status: string | null): 'success' | 'error' | 'warning' {
  switch (status) {
    case 'connected':
      return 'success'
    case 'disconnected':
      return 'error'
    default:
      return 'warning'
  }
}

const statusOrder = ['disconnected', '', 'connected']

function getHeartbeatStatusChipLabel (status: string | null): 'Online' | 'Offline' | 'No Heartbeat Found' {
  switch (status) {
    case 'connected':
      return 'Online'
    case 'disconnected':
      return 'Offline'
    default:
      return 'No Heartbeat Found'
  }
}

function getHeartbeatStatusChipElapsedTime (startTime: string | null, updatedTime: string | null): string {
  // @ts-expect-error null is already accounted for
  return (isEmptyString(startTime) || isEmptyString(updatedTime)) ? '' : formatTimeDifferenceBetweenTwoInstants(startTime, updatedTime)
}

const columns: GridColDef[] = [
  {
    field: 'portfolio',
    headerName: 'Portfolio',
    minWidth: 120,
    sortComparator: (v1, v2) => {
      return v1.localeCompare(v2, undefined, { numeric: true })
    }
  },
  {
    field: 'strategyId',
    headerName: 'Strategy ID',
    minWidth: 170,
    sortComparator: (v1, v2) => {
      return v1.localeCompare(v2, undefined, { numeric: true })
    },
    renderCell: (params) => {
      const urlOrigin = window.location.origin ?? 'http://localhost:3000/'
      const portfolio = params.row.portfolio
      const coin = params.row.coin
      const strategy = params.row.strategyId
      const url = `${urlOrigin}/strategy?portfolio=${portfolio}&coin=${coin}&strategy=${strategy}`

      return <Stack direction='row' gap={1} height={1} alignItems='center'>
        <Tooltip title='View Strategy In New Tab'>
          <IconButton
            sx={{ padding: 0 }}
            onClick={() => {
              window.open(url, '_blank')
            }}
          >
            <ExitToAppIcon/>
          </IconButton>
        </Tooltip>
        <Typography>{params.value}</Typography>
      </Stack>
    }
  },
  {
    field: 'strategyName',
    headerName: 'Strategy Name',
    minWidth: 170,
    valueGetter: (_value, row, _column, _apiRef) => row.strategyName ?? '',
    sortComparator: (v1, v2) => {
      return v1.localeCompare(v2, undefined, { numeric: true })
    },
  },
  {
    field: 'status',
    headerName: 'Heartbeat Status',
    minWidth: 180,
    renderCell: (params) => {
      const colour = getHeartbeatStatusChipColour(params.value)
      const label = getHeartbeatStatusChipLabel(params.value)
      return <Chip size='small' color={colour} label={label}/>
    },
    sortComparator: (v1, v2) => {
      return statusOrder.indexOf(v1) - statusOrder.indexOf(v2)
    }
  },
  {
    field: 'isTradingPaused',
    headerName: 'Trading Status',
    minWidth: 140,
    type: 'boolean',
    renderCell: (params) => {
      if (isEmptyString(params.value)) {
        return <Chip size='small' label='n/a'/>
      }

      const lowercaseValue = params.value.toLowerCase()
      const isTradingPaused = lowercaseValue === 'true'
      const colour = isTradingPaused ? 'error' : 'success'
      const label = isTradingPaused ? 'Paused' : 'Trading'
      return <Stack direction='row' gap={1} alignItems='center'>
        <Chip size='small' color={colour} label={label}/>
        <TradingToggleButton
          strategyName={params.row.strategyId}
          isTradingPaused={isTradingPaused}
          displayToastWithMessage={params.row.displayToastWithMessage}
          controlsEnabled={params.row.controlsEnabled}
        />
      </Stack>
    },
  },
  {
    field: 'uptime',
    headerName: 'Uptime',
    minWidth: 200,
    align: 'right',
    valueGetter: (_value, row, _column, _apiRef) => row.startTime,
    renderCell: (params) => {
      const elapsedTime = getHeartbeatStatusChipElapsedTime(params.row.startTime, params.row.lastUpdateTime)
      if (isEmptyString(elapsedTime)) {
        return null
      }
      if (params.row.status === 'connected') {
        return <Chip size='small' label={elapsedTime}/>
      } else {
        return <Chip size='small' variant='outlined' label={elapsedTime}/>
      }
    },
    sortComparator: (v1, v2) => {
      if (v1 === v2) {
        return 0
      } else if (isEmptyString(v1)) {
        return 1
      } else if (isEmptyString(v2)) {
        return -1
      } else {
        return v1 < v2 ? 1 : -1
      }
    }
  },
  {
    field: 'startTime',
    headerName: 'Start Time',
    minWidth: 220,
    valueFormatter: value => formatToLocalMilliTimestampFromUTCString(value)
  },
  {
    field: 'lastUpdateTime',
    headerName: 'Last Updated Time',
    minWidth: 220,
    valueFormatter: value => formatToLocalMilliTimestampFromUTCString(value)
  }
]

const autosizeOptions: GridAutosizeOptions = {
  columns: ['portfolio', 'strategyId', 'strategyName', 'status', 'isTradingPaused', 'uptime', 'startTime', 'lastUpdateTime'],
  includeOutliers: true,
}

const initialState: GridInitialStatePro = {
  pinnedColumns: { left: ['portfolio', 'strategyId'] },
  sorting: {
    sortModel: [
      { field: 'status', sort: 'asc' },
      { field: 'portfolio', sort: 'asc' },
      { field: 'strategyName', sort: 'asc' },
      { field: 'strategyId', sort: 'asc' }
    ],
  },
}

const StrategyStatusView: React.FC = () => {
  const [enableControlsText, setEnableControlsText] = useState<string>('')
  const [isControlsEnabled, setIsControlsEnabled] = useState<boolean>(false)
  const debouncedEnableControls = useDebouncedCallback((input: boolean) => {
    setIsControlsEnabled(input)
    if (input) {
      displayToastWithMessage('Controls are now enabled', 'success')
    }
  }, 100)
  useEffect(() => {
    const enableControls = enableControlsText === 'enable controls'
    debouncedEnableControls(enableControls)
  }, [enableControlsText])

  const [toastOpen, setToastOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastSeverity, setToastSeverity] = useState<AlertColor>('info')
  const displayToastWithMessage = useCallback((message: string, severity: AlertColor): void => {
    setToastMessage(message)
    setToastSeverity(severity)
    setToastOpen(true)
  }, [])

  const { isAuthorized, accessToken } = useContext(AuthContext)

  const portfolios = usePortfolios(isAuthorized, accessToken)
  const heartbeats = useHeartbeats(isAuthorized, accessToken)

  const [gridRows, setGridRows] = useState<GridRowModelUpdate[]>([])
  useEffect(() => {
    const rows = []

    for (const portfolio in portfolios) {
      const coinToStrategies: Record<string, string[]> = portfolios[portfolio]

      for (const coin in coinToStrategies) {
        const strategies = coinToStrategies[coin].map(strategy => {
          const matchingHeartbeat = heartbeats.find(heartbeat => heartbeat.strategyId === strategy)

          if (matchingHeartbeat != null) {
            return {
              ...matchingHeartbeat,
              portfolio,
              coin,
              displayToastWithMessage,
              controlsEnabled: isControlsEnabled
            }
          } else {
            return {
              strategyId: strategy,
              status: '',
              startTime: '',
              lastUpdateTime: '',
              portfolio,
              coin,
              displayToastWithMessage,
              controlsEnabled: isControlsEnabled
            }
          }
        })

        rows.push(...strategies)
      }
    }

    setGridRows(rows)
  }, [portfolios, heartbeats, displayToastWithMessage, isControlsEnabled])

  const dataGrid = useMemo(() => {
    return (
      <DataGridPro
        rows={gridRows}
        columns={columns}
        getRowId={row => row.strategyId}
        autosizeOptions={autosizeOptions}
        initialState={initialState}
      />
    )
  }, [gridRows])

  const handleControlAll = useCallback((command: string) => {
    const onlineStrategies = heartbeats.filter(heartbeat => heartbeat?.status === 'connected')
    if (onlineStrategies.length === 0) {
      console.debug('[Status] Failed to send command to all strategies because no online strategies were found')
      displayToastWithMessage('No online strategies found - skipping action', 'warning')
      return
    }

    const promises = onlineStrategies.map(async strategyHeartbeat => {
      const strategyName = strategyHeartbeat.strategyId
      const commandUrl = formStrategyApiUrl(strategyName, 'strategy-management/executeCommand')
      const message = {
        timestamp: new Date().toISOString(),
        strategyId: strategyName,
        command,
      }

      console.debug(`[Status] Queuing command for the strategy: ${strategyName}`)
      console.debug(message)

      return await axios.post(commandUrl, message, { headers: { Authorization: `Bearer ${accessToken}` } })
    })

    ExtendedPromise
      .raceSettleAll(promises, 10000, new Error('timeout'))
      .then(response => {
        console.debug(response)
        handleRaceSettleAllResponse(response, false, displayToastWithMessage)
      })
      .catch(error => {
        console.error(error)
        displayToastWithMessage(formAlertErrorMessage(error), 'error')
      })
  }, [heartbeats, displayToastWithMessage])

  return (
    <StackColumnLayout>
      <Grid container spacing={4} padding={4} marginTop={0}>
        <Grid item xs={12}>
          <Typography color='secondary'>
            Firm-wide Controls
          </Typography>
          <Typography variant='subtitle2' gutterBottom>
            To enable controls on this page, type: &apos;enable controls&apos; into the text box below:
          </Typography>
          <Card variant='outlined'>
            <CardContent>
              <Grid container padding={2} spacing={2} alignItems='baseline'>
                <Grid item xs md='auto'>
                  <TextField
                    sx={{ minWidth: { xs: 'none', md: '300px' } }}
                    InputLabelProps={{ shrink: true }}
                    variant='outlined'
                    fullWidth
                    size='small'
                    label='With great power comes great responsibillity'
                    error={isEmptyString(enableControlsText)}
                    value={enableControlsText}
                    onChange={event => {
                      setEnableControlsText(event.target.value)
                    }}
                  />
                </Grid>

                <Grid item xs='auto'>
                  <Button
                    variant='outlined'
                    startIcon={<LockIcon/>}
                    disabled={!isControlsEnabled}
                    onClick={() => {
                      setEnableControlsText('')
                      displayToastWithMessage('Controls are now disabled', 'warning')
                    }}
                  >
                    Lock Controls
                  </Button>
                </Grid>

                <Grid item xs={12}/>

                <Fade in={isControlsEnabled} unmountOnExit>
                  <Grid item xs={6} md='auto'>
                    <Button
                      variant='outlined'
                      startIcon={<PauseCircleOutlineIcon/>}
                      color={isControlsEnabled ? 'error' : 'inherit'}
                      fullWidth
                      disabled={!isControlsEnabled}
                      onClick={() => {
                        handleControlAll('pauseTrading')
                      }}
                    >
                      Pause All
                    </Button>
                  </Grid>
                </Fade>

                <Fade in={isControlsEnabled} unmountOnExit>
                  <Grid item xs={6} md='auto'>
                    <Button
                      variant='outlined'
                      startIcon={<PlayCircleOutlineIcon/>}
                      color={isControlsEnabled ? 'success' : 'inherit'}
                      fullWidth
                      disabled={!isControlsEnabled}
                      onClick={() => {
                        handleControlAll('resumeTrading')
                      }}
                    >
                      Resume All
                    </Button>
                  </Grid>
                </Fade>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Stack gap={2}>
            <Stack direction='row' gap={1} divider={<Divider orientation='vertical' flexItem/>}>
              <Typography variant='subtitle2'>
                Online: {heartbeats.filter(heartbeat => heartbeat.status === 'connected').length}
              </Typography>
              <Typography variant='subtitle2'>
                Offline: {heartbeats.filter(heartbeat => heartbeat.status === 'disconnected').length}
              </Typography>
            </Stack>
            {dataGrid}
          </Stack>
        </Grid>
      </Grid>

      <ToastWithMessage toastOpen={toastOpen} setToastOpen={setToastOpen} message={toastMessage} severity={toastSeverity}/>
    </StackColumnLayout>
  )
}

export default StrategyStatusView
